<script lang="ts">
	import { getArtistSectionCount } from '../../utils/getArtistSectionCount';
	import { setArtistSectionCount } from '../../utils/setArtistSectionCount';
	import { ProjectSectionTitle } from '../ProjectSectionTitle';
	import { SeriesCard } from './SeriesCard';
	import { ARTIST_PROJECTS_LIMIT } from '#lib/websites/artists/pages/ArtistDetailPage/utils/artistDetailsPageLoad/artistDetailsPageLoad.js';
	import { transformCatalogueEditions } from '#lib/websites/artists/pages/ArtistDetailPage/utils/transformCatalogueEditions/index.js';
	import { EditionsRoutes } from '#lib/websites/editions/constants/routes.js';
	import { page } from '$app/state';
	import { Button } from '$global/components/Button';
	import type { ArtistsPageData } from '$routes/artists/[slug]/types';

	interface Props {
		dataCy: string;
	}

	let { dataCy }: Props = $props();

	let dataCyPrefix = $derived(`${dataCy}-editions`);
	let artist = $derived(page.data.artist as ArtistsPageData['artist']);
	let catalogue = $derived(page.data.catalogue as ArtistsPageData['catalogue']);

	// One card per series, plus a card for each edition that belongs to no
	// series. A series is never broken down into its individual editions.
	let items = $derived(
		transformCatalogueEditions(catalogue?.series, catalogue?.serieslessEditions)
	);
	let visibleCount = $state(
		getArtistSectionCount('editions') ?? ARTIST_PROJECTS_LIMIT
	);
	let visibleItems = $derived(items.slice(0, visibleCount));
	// The editions gallery supports filtering by artist id (filter key 1,
	// string-array type 3), so the full set is one click away.
	let viewAllHref = $derived(
		`${EditionsRoutes.Gallery}?1=${artist.id}&1_type=3`
	);
	let canViewMore = $derived(visibleCount < items.length);

	// Same-route navigations reuse this component instance, so re-read the new
	// history entry's persisted count (or fall back to the default) whenever the
	// artist — or, on back/forward, the entry's state — changes.
	$effect(() => {
		// eslint-disable-next-line @typescript-eslint/no-unused-expressions
		artist.id;
		visibleCount = getArtistSectionCount('editions') ?? ARTIST_PROJECTS_LIMIT;
	});

	const showMore = () => {
		visibleCount += ARTIST_PROJECTS_LIMIT;
		setArtistSectionCount('editions', visibleCount);
	};
</script>

{#if items.length > 0}
	<div class="border-b border-gray-200"></div>
	<div>
		<ProjectSectionTitle
			title="EDITIONS"
			href={viewAllHref}
			dataCy={dataCyPrefix}
			label="View all editions"
		/>
		<div
			class="mb-8 grid grid-cols-1 gap-4 sm:grid-cols-2 md:grid-cols-3 md:gap-6 lg:grid-cols-4 lg:gap-8"
		>
			{#each visibleItems as item (item.href)}
				<SeriesCard {dataCy} {item} />
			{/each}
		</div>
		{#if canViewMore}
			<Button
				dataCy={`${dataCyPrefix}-view-more`}
				variant="secondary"
				size="md"
				class="mx-auto mb-8 w-full md:w-fit"
				onclick={showMore}>Show more</Button
			>
		{/if}
	</div>
{/if}
